import type { Account, HealthBand, Workspace } from "./types";

export const bandLabel: Record<HealthBand, string> = {
  good: "Healthy",
  watch: "In the penumbra",
  risk: "At risk",
};

export const bandFromScore = (h: number): HealthBand => (h >= 70 ? "good" : h >= 45 ? "watch" : "risk");

/** Weighted recompute of a health score from its factors — mirrors the API. */
export function computeHealth(a: Account): number {
  const total = a.scoreFactors.reduce((s, f) => s + f.weight, 0) || 1;
  const score = a.scoreFactors.reduce((s, f) => s + f.value * f.weight, 0) / total;
  return Math.round(score);
}

export interface PortfolioSummary {
  totalArr: number;
  atRiskArr: number;
  renewingArr: number;
  nrr: number;
  grr: number;
  avgHealth: number;
  bands: Record<HealthBand, number>;
  openSignals: number;
  renewals90: number;
}

export function summarize(ws: Workspace, today = new Date("2026-08-16T12:00:00Z")): PortfolioSummary {
  const accts = ws.accounts;
  const totalArr = accts.reduce((s, a) => s + a.arr, 0);
  const atRiskArr = accts.filter((a) => a.healthBand === "risk").reduce((s, a) => s + a.arr, 0);
  const in90 = (iso: string) => {
    const d = (new Date(iso).getTime() - today.getTime()) / 86400000;
    return d >= 0 && d <= 90;
  };
  const renewing = accts.filter((a) => in90(a.renewalDate));
  const renewingArr = renewing.reduce((s, a) => s + a.arr, 0);
  const bands: Record<HealthBand, number> = { good: 0, watch: 0, risk: 0 };
  accts.forEach((a) => (bands[a.healthBand] += 1));
  // Guard the empty book (a brand-new account): no accounts → 0 avg health,
  // no ARR → 100% retention (nothing to churn), never NaN.
  const avgHealth = accts.length ? Math.round(accts.reduce((s, a) => s + a.health, 0) / accts.length) : 0;

  // model NRR/GRR from health + expansion potential (illustrative but stable)
  const expansion = accts.reduce((s, a) => s + (a.expansionPotential / 100) * a.arr * 0.18, 0);
  const churnRisk = accts.reduce((s, a) => s + (a.healthBand === "risk" ? a.arr * 0.35 : a.healthBand === "watch" ? a.arr * 0.08 : 0), 0);
  const grr = totalArr ? Math.round(((totalArr - churnRisk) / totalArr) * 100) : 100;
  const nrr = totalArr ? Math.round(((totalArr - churnRisk + expansion) / totalArr) * 100) : 100;

  return {
    totalArr, atRiskArr, renewingArr, nrr, grr, avgHealth, bands,
    openSignals: ws.signals.filter((s) => s.status !== "resolved").length,
    renewals90: renewing.length,
  };
}
